import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GlassCard } from './Layout';
import { Bell, X, ArrowRight, Clock, Coins, GraduationCap, ListChecks, Compass } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const URGENCY = {
  passed: { hue: '#f87171', label: 'Overdue' },
  now: { hue: '#fbbf24', label: 'This week' },
  soon: { hue: '#22d3ee', label: 'This month' },
  later: { hue: '#94a3b8', label: 'Coming up' },
};

const ICON = {
  funding: Coins, exams: GraduationCap, opportunities: Compass, plan: ListChecks,
};

/** What's next. Deadlines, follow-ups and the things quietly slipping. */
export const NudgePanel = () => {
  const { getAuthHeader } = useAuth();
  const [d, setD] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/nudges`, getAuthHeader());
      setD(r.data);
    } catch { setD(null); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (id) => {
    setD((p) => ({ ...p, nudges: p.nudges.filter((n) => n.id !== id) }));
    axios.post(`${API}/nudges/${encodeURIComponent(id)}/dismiss`, {}, getAuthHeader()).catch(() => {});
  };

  if (!d?.nudges?.length) return null;
  const shown = showAll ? d.nudges : d.nudges.slice(0, 4);

  return (
    <GlassCard className="mb-6 scroll-mt-6" id="whats-next" data-testid="nudge-panel">
      <div className="flex flex-wrap items-center gap-3">
        <span className="relative flex h-8 w-8 items-center justify-center rounded-xl bg-amber-400/15 border border-amber-300/30">
          <Bell className="w-4 h-4 text-amber-300" />
          {d.attention > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-[10px]
                             font-bold text-white flex items-center justify-center">{d.attention}</span>
          )}
        </span>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-amber-200/90">What's next</p>
          <h2 className="font-outfit text-lg font-semibold text-white leading-tight">
            {d.counts.passed > 0
              ? `${d.counts.passed} thing${d.counts.passed > 1 ? 's' : ''} slipped. Here's what needs you.`
              : d.counts.now > 0
                ? `${d.counts.now} deadline${d.counts.now > 1 ? 's' : ''} this week.`
                : `${d.total} deadline${d.total > 1 ? 's' : ''} on your calendar.`}
          </h2>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mt-4">
        {shown.map((n) => {
          const u = URGENCY[n.urgency] || URGENCY.later;
          const Icon = ICON[n.category] || Clock;
          return (
            <div key={n.id} className="group relative rounded-xl border p-3.5 pr-9 transition-colors duration-200"
              style={{ borderColor: `${u.hue}33`, background: `linear-gradient(150deg, ${u.hue}12, rgba(255,255,255,0.02))` }}
              data-testid={`nudge-${n.id}`}>
              <button onClick={() => dismiss(n.id)} data-testid={`nudge-dismiss-${n.id}`}
                aria-label="Dismiss"
                className="absolute top-2.5 right-2.5 text-slate-300 hover:text-white transition-colors duration-200">
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="flex items-center gap-2">
                <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: u.hue }} />
                <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: u.hue }}>
                  {u.label} · {n.when}
                </span>
              </div>
              <p className="text-sm font-semibold text-white mt-1.5 leading-snug">{n.title}</p>
              <p className="text-xs text-slate-300/85 mt-1">{n.body}</p>
              <p className="text-[11px] text-slate-300 mt-1.5 italic">{n.why_now}</p>
              <Link to={n.path} data-testid={`nudge-cta-${n.id}`}
                className="inline-flex items-center gap-1 text-[11px] mt-2.5 hover:gap-2 transition-all duration-200"
                style={{ color: u.hue }}>
                {n.cta} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          );
        })}
      </div>

      {d.nudges.length > 4 && (
        <button onClick={() => setShowAll(!showAll)} data-testid="nudge-show-all"
          className="mt-3 text-xs text-slate-300 hover:text-white transition-colors duration-200">
          {showAll ? 'Show fewer' : `Show all ${d.nudges.length}`}
        </button>
      )}
    </GlassCard>
  );
};

/**
 * The deadline count, in the Your Path bar. It says what it is, "8 deadlines coming up",
 * not a bare number nobody can decode, and jumps to What's Next on the dashboard.
 */
export const DeadlineChip = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [n, setN] = useState(null);

  useEffect(() => {
    axios.get(`${API}/nudges`, getAuthHeader()).then((r) => setN(r.data)).catch(() => {});
  }, [getAuthHeader]);

  if (!n?.total) return null;
  const overdue = n.counts?.passed || 0;
  const thisWeek = n.counts?.now || 0;
  const urgent = overdue > 0 || thisWeek > 0;
  const hue = overdue > 0 ? '#f87171' : thisWeek > 0 ? '#fbbf24' : '#22d3ee';

  const label = overdue > 0
    ? `${overdue} overdue · ${n.total} deadlines`
    : thisWeek > 0
      ? `${thisWeek} deadline${thisWeek > 1 ? 's' : ''} this week`
      : `${n.total} deadlines coming up`;

  const go = (e) => {
    e.preventDefault();
    const reveal = () => {
      const el = document.getElementById('whats-next');
      if (!el) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('fp-flash');
      setTimeout(() => el.classList.remove('fp-flash'), 1600);
      return true;
    };
    if (reveal()) return;
    navigate('/app/dashboard');
    // The dashboard mounts its panels asynchronously, try until it is there.
    let tries = 0;
    const t = setInterval(() => { if (reveal() || ++tries > 20) clearInterval(t); }, 200);
  };

  return (
    <a href="/app/dashboard" onClick={go} data-testid="deadline-chip"
      title="Deadlines, applications and follow-ups waiting on you"
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors duration-200 hover:brightness-125"
      style={{ borderColor: `${hue}55`, background: `${hue}14`, color: hue }}>
      <Bell className={`w-3 h-3 ${urgent ? 'animate-pulse' : ''}`} />
      {label}
    </a>
  );
};

export default NudgePanel;
